import React, { useEffect, useState } from 'react';
import { invitationsAPI } from '../../services/api';
import CharacterSelectionModal from './CharacterSelectionModal';
import CharacterCreationModal from './CharacterCreationModal';
import './CampaignInvitations.css';

function CampaignInvitations({ onCharacterCreated }) {
  const [invitations, setInvitations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [respondingTo, setRespondingTo] = useState(null);

  // Character flow state
  const [showCharacterSelection, setShowCharacterSelection] = useState(false);
  const [showCharacterCreation, setShowCharacterCreation] = useState(false);

  // Campaign context for the invite flow (MUST remain stable until flow ends)
  const [selectedCampaignData, setSelectedCampaignData] = useState(null);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await invitationsAPI.getMyInvitations();
      setInvitations(res.data?.invitations || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
      setError('Failed to load invitations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitation) => {
    try {
      setRespondingTo(invitation.id);
      setError(null);

      const res = await invitationsAPI.respondToInvitation(invitation.id, 'accept');

      // Authoritative campaign data (response preferred; invitation fallback)
      const campaignId = res.data?.campaignId || invitation.campaignId;
      const campaignName = res.data?.campaignName || invitation.campaignName;
      const startingXp = (res.data?.startingXp ?? invitation.startingXp ?? 1000);

      if (!campaignId) {
        setError('Invite accepted, but campaignId was missing. Please refresh and try again.');
        return;
      }

      setSelectedCampaignData({ campaignId, campaignName, startingXp });

      // Update local status to accepted so the user can resume later if needed
      setInvitations(prev => prev.map(inv => (
        inv.id === invitation.id
          ? { ...inv, status: 'accepted', respondedAt: new Date().toISOString() }
          : inv
      )));

      setShowCharacterSelection(true);
    } catch (err) {
      console.error('Failed to accept invitation:', err);
      setError(err?.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setRespondingTo(null);
    }
  };

  const handleContinueAcceptedInvite = (invitation) => {
    const campaignId = invitation.campaignId;
    const campaignName = invitation.campaignName;
    const startingXp = (invitation.startingXp ?? 1000);

    if (!campaignId) {
      setError('Campaign context missing for this invitation. Please refresh and try again.');
      return;
    }

    setSelectedCampaignData({ campaignId, campaignName, startingXp });
    setShowCharacterSelection(true);
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      setRespondingTo(invitationId);
      setError(null);

      await invitationsAPI.respondToInvitation(invitationId, 'decline');
      setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
    } catch (err) {
      console.error('Failed to decline invitation:', err);
      setError(err?.response?.data?.error || 'Failed to decline invitation');
    } finally {
      setRespondingTo(null);
    }
  };

  // Selecting an existing character must attach it on the backend
  const handleCharacterSelected = async (character) => {
    try {
      setError(null);

      const campaignId = selectedCampaignData?.campaignId;
      if (!campaignId) {
        setError('Campaign context missing. Please refresh and try again.');
        return;
      }

      if (!invitationsAPI.attachExistingCharacterToCampaign) {
        setError('attachExistingCharacterToCampaign is missing in invitationsAPI (services/api.js).');
        return;
      }

      await invitationsAPI.attachExistingCharacterToCampaign(campaignId, character.id);

      setShowCharacterSelection(false);

      // Refresh invitations so accepted invites that are now "joined" disappear
      await loadInvitations();

      onCharacterCreated?.({
        ...character,
        campaignId,
        campaignName: selectedCampaignData?.campaignName
      });

      setSelectedCampaignData(null);
    } catch (err) {
      console.error('Failed to attach existing character to campaign:', err);
      setError(err?.response?.data?.error || 'Failed to join campaign with selected character');
    }
  };

  const handleCharacterCreated = (character) => {
    setShowCharacterCreation(false);

    onCharacterCreated?.(character);

    // Refresh invitations so accepted invites that are now "joined" disappear
    loadInvitations();

    setSelectedCampaignData(null);
  };

  const handleCreateNewCharacter = () => {
    if (!selectedCampaignData?.campaignId) {
      setError('Cannot create a campaign character without campaign context. Please refresh and try again.');
      return;
    }

    setShowCharacterSelection(false);
    setShowCharacterCreation(true);
  };

  // Actionable invites:
  // - pending: can accept/decline
  // - accepted but not yet joined: can resume character select/create
  const actionableInvitations = invitations.filter(inv =>
    inv.status === 'pending' || (inv.status === 'accepted' && !inv.hasJoined)
  );

  const shouldHideEntireComponent =
    actionableInvitations.length === 0 &&
    !showCharacterSelection &&
    !showCharacterCreation;

  if (isLoading) {
    return (
      <div className="invitations-section">
        <h3>Campaign Invitations</h3>
        <p className="loading-text">Loading invitations...</p>
      </div>
    );
  }

  if (shouldHideEntireComponent) return null;

  return (
    <>
      {actionableInvitations.length > 0 && (
        <div className="invitations-section">
          <h3>Campaign Invitations</h3>

          {error && <div className="error-banner">{error}</div>}

          <div className="invitations-list">
            {actionableInvitations.map(invitation => (
              <div key={invitation.id} className="invitation-card">
                <div className="invitation-header">
                  <h4>{invitation.campaignName}</h4>
                  <span className="invitation-date">
                    {new Date(invitation.createdAt).toLocaleDateString()}
                  </span>
                </div>

                <div className="invitation-details">
                  <p className="invitation-from">
                    Invited by <strong>{invitation.invitedByName}</strong> (GM: {invitation.gmName})
                  </p>

                  {invitation.message && (
                    <div className="invitation-message">
                      <p className="message-label">Message:</p>
                      <p className="message-text">"{invitation.message}"</p>
                    </div>
                  )}

                  {invitation.campaignDescription && (
                    <div className="campaign-description">
                      <p className="description-label">Campaign Description:</p>
                      <p className="description-text">{invitation.campaignDescription}</p>
                    </div>
                  )}

                  <div className="campaign-info">
                    <span className="info-item">
                      Starting XP: <strong>{invitation.startingXp ?? 1000}</strong>
                    </span>
                  </div>
                </div>

                <div className="invitation-actions">
                  {invitation.status === 'pending' ? (
                    <>
                      <button
                        className="btn-primary accept-btn"
                        onClick={() => handleAcceptInvitation(invitation)}
                        disabled={respondingTo === invitation.id}
                      >
                        {respondingTo === invitation.id ? 'Accepting...' : 'Accept Invitation'}
                      </button>
                      <button
                        className="btn-secondary decline-btn"
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        disabled={respondingTo === invitation.id}
                      >
                        {respondingTo === invitation.id ? 'Declining...' : 'Decline'}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn-primary accept-btn"
                      onClick={() => handleContinueAcceptedInvite(invitation)}
                    >
                      Continue Joining
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <CharacterSelectionModal
        isOpen={showCharacterSelection}
        onClose={() => {
          setShowCharacterSelection(false);
        }}
        campaignData={selectedCampaignData}
        onCharacterSelected={handleCharacterSelected}
        onCreateNew={handleCreateNewCharacter}
      />

      <CharacterCreationModal
        isOpen={showCharacterCreation}
        onClose={() => {
          setShowCharacterCreation(false);
          setShowCharacterSelection(true);
        }}
        campaignData={selectedCampaignData}
        onCharacterCreated={handleCharacterCreated}
      />
    </>
  );
}

export default CampaignInvitations;
